/**
 * Which files were finished a moment ago, according to the channel.
 *
 * **Why the grid has to know.** A card that mounts on a file nobody made a
 * thumbnail for asks to make one, and the moment a file finishes uploading is
 * exactly when that is true of every file: the uploader is making the
 * thumbnail right now, from the copy in its memory, and the server will say so
 * with `node.thumbnail_ready` within seconds. A second person's browser that
 * downloaded the original to do the same work would be spending a download, a
 * decode and an upload to lose the race — the server refuses the second
 * thumbnail with a `412`, and that is the good outcome. So a card that sees a
 * file uploaded in the last `RECENT_UPLOAD_MS` waits for the event instead.
 *
 * **Ten seconds is a bound on the wait, not on the work.** The uploader's
 * thumbnail normally lands in one or two; a browser that cannot decode the
 * format never produces one at all, and past the window the next card that
 * asks is allowed to try, which is the whole reason the wait is bounded.
 *
 * The clock is a parameter so the rule can be tested without waiting for it.
 */
export const RECENT_UPLOAD_MS = 10_000

export function createRecentUploads(now: () => number = Date.now) {
	const seen = new Map<string, number>()

	return {
		/** Record that `node.uploaded` named this node just now. */
		note(nodeId: string) {
			seen.set(nodeId, now())
		},

		/** Whether the channel named this node within the last ten seconds. */
		wasRecent(nodeId: string) {
			const at = seen.get(nodeId)

			if (at === undefined) {
				return false
			}

			if (now() - at >= RECENT_UPLOAD_MS) {
				// Dropped on the way past so the map does not grow with every
				// upload of a long session.
				seen.delete(nodeId)
				return false
			}

			return true
		}
	}
}

/** The one record of this tab, written by the directory feed's consumer. */
export const recentUploads = createRecentUploads()
